import { Button, Topbar } from '@domcrm/ui';
import { ChevronsLeft, ChevronsRight } from 'lucide-react';
import { isStaffPlatformMode } from '../../shared/access/staff-platform-mode';
import { useAuth } from '../../shared/auth/use-auth';
import { StaffThemeToggle } from '../../shared/theme/StaffThemeToggle';
import { StaffImpersonationBadge } from './staff-impersonation-badge';
import { StaffOrgSelector } from './staff-org-selector';
import { StaffProfileMenu } from './StaffProfileMenu';
import { staffTopbarOrgContextPlaqueReplacesCenterPicker } from './staff-topbar-org-context';

export type StaffTopbarProps = {
  onNavToggle?: () => void;
  /** Мобильное меню навигации открыто (для `aria-expanded` и подписи кнопки). */
  navOpen?: boolean;
  sidebarCollapsed?: boolean;
  onSidebarCollapseToggle?: () => void;
};

function MenuGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden focusable="false">
      <path
        d="M4 6h16M4 12h16M4 18h16"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function StaffTopbar({
  onNavToggle,
  navOpen = false,
  sidebarCollapsed,
  onSidebarCollapseToggle
}: StaffTopbarProps) {
  const { me, impersonateOrgId, logout } = useAuth();

  const platformMode = isStaffPlatformMode(me, impersonateOrgId);
  const hideCenterOrgPicker = staffTopbarOrgContextPlaqueReplacesCenterPicker(me, impersonateOrgId);

  return (
    <Topbar
      data-testid="staff-topbar"
      className="border-b border-[hsl(var(--ui-border))] bg-[hsl(var(--ui-background)/0.78)] backdrop-blur-md supports-[backdrop-filter]:bg-[hsl(var(--ui-background)/0.65)]"
      left={
        <>
          <Button
            type="button"
            variant="ghost"
            data-testid="staff-nav-toggle"
            className="md:hidden h-9 w-9 shrink-0 px-0"
            aria-label={navOpen ? 'Закрыть меню' : 'Открыть меню'}
            aria-expanded={navOpen}
            aria-controls="staff-sidebar-nav"
            onClick={() => onNavToggle?.()}
          >
            <MenuGlyph />
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="hidden h-9 w-9 shrink-0 px-0 md:inline-flex"
            aria-label={sidebarCollapsed ? 'Развернуть меню' : 'Свернуть меню'}
            onClick={() => onSidebarCollapseToggle?.()}
          >
            {sidebarCollapsed ? (
              <ChevronsRight className="size-[18px]" aria-hidden />
            ) : (
              <ChevronsLeft className="size-[18px]" aria-hidden />
            )}
          </Button>
          <span className="hidden truncate text-sm font-semibold tracking-tighter text-[hsl(var(--ui-foreground))] sm:inline">
            DomCRM · Персонал
          </span>
        </>
      }
      center={
        hideCenterOrgPicker ? null : platformMode ? (
          <div className="flex min-h-9 w-full min-w-0 flex-1 items-center justify-center px-2 md:max-w-[min(560px,90vw)]">
            <div className="min-w-0 flex-1 sm:max-w-[min(360px,50vw)]">
              <StaffOrgSelector className="w-full min-w-0" platformTopbarPicker />
            </div>
          </div>
        ) : (
          <div className="flex w-full min-w-0 flex-1 flex-wrap items-center justify-center gap-2 md:max-w-[min(480px,55vw)]">
            <div className="w-full min-w-0 flex-1 md:max-w-[min(420px,50vw)]">
              <StaffOrgSelector className="w-full min-w-0" />
            </div>
          </div>
        )
      }
      right={
        <>
          <StaffImpersonationBadge />
          <StaffProfileMenu />
          <StaffThemeToggle className="hidden h-9 w-9 shrink-0 px-0 lg:inline-flex" />
          <Button
            type="button"
            variant="secondary"
            className="hidden shrink-0 lg:inline-flex"
            onClick={() => logout()}
          >
            Выход
          </Button>
        </>
      }
    />
  );
}
